// src/pages/CourseTime/hooks/useActivityInteractions.js
import { useState, useEffect } from 'react';
import { recordInteraction, getInteraction, updateContentHistory, fetchLearningProgramOutline } from '../../../services/learning_program.service';
import { LOCAL_STORAGE_KEYS } from '../../../utils/constants';

// Interaction configuration mapping slide layout types to interaction types and requirements
const INTERACTION_CONFIG = {
    'JOURNALING_SELF_REFLECTION_ACTIVITY': {
        type: 'TEXT',
        required: true,
        show_in_widget: true
    },
    'SHARE_WORK_FILE_ACTIVITY': {
        type: 'TEXT',
        required: false,
        show_in_widget: true
    },
    'POLL_QUIZ_ACTIVITY': {
        type: 'JSON',
        required: true,
        show_in_widget: true
    },
    'DISCUSSION_ACTIVITY': {
        type: 'JSON',
        required: false
    },
    'ASSESSMENT_ACTIVITY': {
        type: 'JSON',
        required: true
    }
};

export const useActivityInteractions = (
  identifier,
  profile,
  activeSessionNo,
  activeSegment,
  currentSlideIndex,
  sessionContent,
  onDiscussionModalOpen = null,
  onSessionDataUpdate = null,
  onDiscussionModalOpenWithNewSession = null
) => {
  // Activity-related state
  const [journalingText, setJournalingText] = useState('');
  const [shareWorkLink, setShareWorkLink] = useState('');
  const [selectedPollOption, setSelectedPollOption] = useState(null);
  const [selectedPollOptions, setSelectedPollOptions] = useState([]);
  const [assessmentAnswers, setAssessmentAnswers] = useState({});
  const [activityCompleted, setActivityCompleted] = useState({});
  const [interactionLoading, setInteractionLoading] = useState(false);
  const [fetchingExistingRecord, setFetchingExistingRecord] = useState(false);
  const [inputsDisabled, setInputsDisabled] = useState(false);
  const [currentSlideInteraction, setCurrentSlideInteraction] = useState(null);

  // Helper function to get profile from localStorage if not available
  const getProfileFromStorage = () => {
    try {
      const org_code = localStorage.getItem(LOCAL_STORAGE_KEYS.ACTIVE_ORG_CODE);
      if (org_code) {
        const available_profiles = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.AVAILABLE_PROFILES));
        if (available_profiles) {
          return available_profiles.find(profile => profile.org_code === org_code);
        }
      }
    } catch (error) {
      console.error('Error loading profile from localStorage:', error);
    }
    return null;
  };

  // Reset activity state when changing slides
  const resetActivityState = () => {
    setJournalingText('');
    setShareWorkLink('');
    setSelectedPollOption(null);
    setSelectedPollOptions([]);
    setAssessmentAnswers({});
    setCurrentSlideInteraction(null);
    setInteractionLoading(false);
    setFetchingExistingRecord(false);
    setInputsDisabled(false);
  };

  // Activity handlers
  const handleJournalingSave = async (slide) => {
    // Get profile - either from props or localStorage
    let currentProfile = profile;
    if (!currentProfile?._id) {
      currentProfile = getProfileFromStorage();
    }
    
    if (!journalingText.trim() || !currentProfile?._id || !identifier || !activeSessionNo || !activeSegment) return;

    try {
      setInteractionLoading(true);
      const response = await recordInteraction(
        identifier,
        currentProfile._id,
        activeSessionNo,
        activeSegment,
        currentSlideIndex,
        slide.SlideLayout,
        journalingText.trim(),
        INTERACTION_CONFIG[slide.SlideLayout].type
      );

      if (response.data) {
        setActivityCompleted(prev => ({ ...prev, [slide.SlideID]: true }));
        setCurrentSlideInteraction(response.data.interaction);
        setInputsDisabled(true); // Disable inputs after successful save
        console.log('Journaling saved successfully:', response.data);
      }
    } catch (error) {
      console.error('Error saving journaling:', error);
    } finally {
      setInteractionLoading(false);
    }
  };

  const handleShareWorkSubmit = async (slide) => {
    // Get profile - either from props or localStorage
    let currentProfile = profile;
    if (!currentProfile?._id) {
      currentProfile = getProfileFromStorage();
    }
    
    if (!shareWorkLink.trim() || !currentProfile?._id || !identifier || !activeSessionNo || !activeSegment) return;

    try {
      setInteractionLoading(true);
      const response = await recordInteraction(
        identifier,
        currentProfile._id,
        activeSessionNo,
        activeSegment,
        currentSlideIndex,
        slide.SlideLayout,
        shareWorkLink.trim(),
        INTERACTION_CONFIG[slide.SlideLayout].type
      );

      if (response.data) {
        setActivityCompleted(prev => ({ ...prev, [slide.SlideID]: true }));
        setCurrentSlideInteraction(response.data.interaction);
        setInputsDisabled(true); // Disable inputs after successful save
        console.log('Share work submitted successfully:', response.data);
      }
    } catch (error) {
      console.error('Error submitting share work:', error);
    } finally {
      setInteractionLoading(false);
    }
  };

  const handlePollSubmit = async (slide) => {
    const isMAQ = slide.Category11 === 'MAQ';
    const hasValidSelection = isMAQ 
      ? selectedPollOptions.length > 0 
      : selectedPollOption !== null;

    // Get profile - either from props or localStorage
    let currentProfile = profile;
    if (!currentProfile?._id) {
      currentProfile = getProfileFromStorage();
    }

    if (!hasValidSelection || !currentProfile?._id || !identifier || !activeSessionNo || !activeSegment) return;

    try {
      setInteractionLoading(true);
      
      let pollData;
      if (isMAQ) {
        // Multiple Answer Question
        pollData = {
          selectedOptions: selectedPollOptions,
          selectedTexts: selectedPollOptions.map(idx => slide.Options[idx]),
          category11: slide.Category11,
          assess10: slide.Assess10,
          timestamp: new Date().toISOString()
        };
      } else {
        // Multiple Choice Question (single selection)
        pollData = {
          selectedOption: selectedPollOption,
          selectedText: slide.Options[selectedPollOption],
          category11: slide.Category11,
          assess10: slide.Assess10,
          timestamp: new Date().toISOString()
        };
      }

      const response = await recordInteraction(
        identifier,
        currentProfile._id,
        activeSessionNo,
        activeSegment,
        currentSlideIndex,
        slide.SlideLayout,
        pollData,
        INTERACTION_CONFIG[slide.SlideLayout].type
      );

      if (response.data) {
        setActivityCompleted(prev => ({ ...prev, [slide.SlideID]: true }));
        setCurrentSlideInteraction(response.data.interaction);
        setInputsDisabled(true); // Disable inputs after successful save
        console.log('Poll submitted successfully:', response.data);
      }
    } catch (error) {
      console.error('Error submitting poll:', error);
    } finally {
      setInteractionLoading(false);
    }
  };

  const handleAssessmentSubmit = async (slide, answersMap) => {
    // Get profile - either from props or localStorage
    let currentProfile = profile;
    if (!currentProfile?._id) {
      currentProfile = getProfileFromStorage();
    }
    
    if (!answersMap || Object.keys(answersMap).length === 0 || !currentProfile?._id || !identifier || !activeSessionNo || !activeSegment) return;

    try {
      setInteractionLoading(true);
      
      const assessment = slide.SlideContent || {};
      const questionType = assessment.category_11 || 'MCQ';
      const slideId = slide.SlideID;
      
      // Get the answer for this specific slide
      const currentAnswer = answersMap[slideId];
      if (!currentAnswer && currentAnswer !== 0) return;
      
      // Prepare assessment data similar to poll data structure
      let assessmentData;
      
      if (questionType === 'MAQ') {
        // Multiple Answer Question
        const selectedOptions = Array.isArray(currentAnswer) ? currentAnswer : [];
        let options = [];
        try {
          options = typeof assessment.instrument_response === 'string'
            ? JSON.parse(assessment.instrument_response)
            : assessment.instrument_response;
          if (!Array.isArray(options)) options = [options];
        } catch (e) {
          console.error('Error parsing assessment options:', e);
          options = [];
        }
        
        assessmentData = {
          selectedOptions: selectedOptions,
          selectedTexts: selectedOptions.map(idx => options[idx]),
          questionType: questionType,
          assessmentType: slide.assessment_type,
          assess10: slide.assess_10,
          timestamp: new Date().toISOString()
        };
      } else if (questionType === 'MTQ') {
        // Matching/Ordering Question — the answer IS the arrangement:
        // selectedOptions[displayPosition] = originalChoiceIndex.
        const order = Array.isArray(currentAnswer) ? currentAnswer : [];
        if (order.length === 0) return;
        let options = [];
        try {
          options = typeof assessment.instrument_response === 'string'
            ? JSON.parse(assessment.instrument_response)
            : assessment.instrument_response;
          if (!Array.isArray(options)) options = [options];
        } catch (e) {
          console.error('Error parsing assessment options:', e);
          options = [];
        }

        assessmentData = {
          selectedOptions: order,
          selectedTexts: order.map(idx => options[idx]),
          questionType: questionType,
          assessmentType: slide.assessment_type,
          assess10: slide.assess_10,
          timestamp: new Date().toISOString()
        };
      } else if (questionType === 'OPQ' && typeof currentAnswer === 'string') {
        // Open Question (Free text)
        assessmentData = {
          freeTextAnswer: currentAnswer,
          questionType: questionType,
          assessmentType: slide.assessment_type,
          assess10: slide.assess_10,
          timestamp: new Date().toISOString()
        };
      } else if (typeof currentAnswer === 'object' && currentAnswer.customInput) {
        // Custom input case
        assessmentData = {
          customInput: currentAnswer.customInput,
          selectedIndex: currentAnswer.selectedIndex,
          questionType: questionType,
          assessmentType: slide.assessment_type,
          assess10: slide.assess_10,
          timestamp: new Date().toISOString()
        };
      } else {
        // Multiple Choice Question (single selection)
        let options = [];
        try {
          options = typeof assessment.instrument_response === 'string'
            ? JSON.parse(assessment.instrument_response)
            : assessment.instrument_response;
          if (!Array.isArray(options)) options = [options];
        } catch (e) {
          console.error('Error parsing assessment options:', e);
          options = [];
        }
        
        assessmentData = {
          selectedOption: currentAnswer,
          selectedText: options[currentAnswer],
          questionType: questionType,
          assessmentType: slide.assessment_type,
          assess10: slide.assess_10,
          timestamp: new Date().toISOString()
        };
      }

      const response = await recordInteraction(
        identifier,
        currentProfile._id,
        activeSessionNo,
        activeSegment,
        currentSlideIndex,
        slide.SlideLayout,
        assessmentData,
        INTERACTION_CONFIG[slide.SlideLayout].type
      );

      if (response.data) {
        setActivityCompleted(prev => ({ ...prev, [slide.SlideID]: true }));
        setCurrentSlideInteraction(response.data.interaction);
        setInputsDisabled(true);
        setInteractionLoading(false);
      } else {
        setInteractionLoading(false);
        throw new Error('Failed to submit assessment - no data returned');
      }
    } catch (error) {
      console.error('Error submitting assessment:', error);
      setInteractionLoading(false);
      throw error;
    }
  };

  const handleDiscussionStart = async (slide) => {
    // Get profile - either from props or localStorage
    let currentProfile = profile;
    if (!currentProfile?._id) {
      currentProfile = getProfileFromStorage();
    }
    
    if (!currentProfile?._id || !identifier || !activeSessionNo || !activeSegment) return;

    try {
      setInteractionLoading(true);
      
      let currentDiscussionSessions = [];
      
      try {
        // Fetch current program outline to get existing discussion sessions
        const outlineResponse = await fetchLearningProgramOutline(identifier, currentProfile._id);
        if (outlineResponse.data && outlineResponse.data.discussion_sessions) {
          currentDiscussionSessions = outlineResponse.data.discussion_sessions;
        }
      } catch (fetchError) {
        console.warn('Could not fetch current discussion sessions, assuming empty array:', fetchError);
        currentDiscussionSessions = [];
      }
      
      // Check if a discussion session for this slide already exists
      const existingSession = currentDiscussionSessions.find(
        session => session.slide_id === slide.SlideID
      );
      
      if (existingSession) {
        console.log('Discussion session already exists for this slide:', existingSession);
        setActivityCompleted(prev => ({ ...prev, [slide.SlideID]: true }));

        // Sessions created before slide_content was tracked won't have it -
        // backfill it here so old sessions show the description too.
        if (!existingSession.slide_content && slide.SlideContent) {
          const patchedDiscussionSessions = currentDiscussionSessions.map(session =>
            session.slide_id === slide.SlideID
              ? { ...session, slide_content: slide.SlideContent }
              : session
          );

          try {
            await updateContentHistory(identifier, currentProfile._id, {
              discussion_sessions: patchedDiscussionSessions
            });
            if (onSessionDataUpdate) {
              onSessionDataUpdate({ discussionSessions: patchedDiscussionSessions });
            }
          } catch (patchError) {
            console.warn('Could not backfill slide_content for existing discussion session:', patchError);
          }
        }

        // Open discussion modal since session already exists
        // Pass the slide_id to open the correct tab
        if (onDiscussionModalOpen) {
          onDiscussionModalOpen(slide.SlideID);
        }
        return;
      }

      // Create new discussion session entry
      const newDiscussionSession = {
        slide_id: slide.SlideID,
        slide_title: slide.SlideTitle,
        slide_content: slide.SlideContent,
        order_no: currentDiscussionSessions.length + 1
      };
      
      // Create updated discussion sessions array
      const updatedDiscussionSessions = [...currentDiscussionSessions, newDiscussionSession];
      
      // Update content history with the complete discussion sessions array
      const historyData = {
        discussion_sessions: updatedDiscussionSessions
      };
      
      const response = await updateContentHistory(identifier, profile._id, historyData);
      
      if (response.success || response.data) {
        // Record the interaction as well
        const interactionData = {
          discussion_started: true,
          slide_id: slide.SlideID,
          slide_title: slide.SlideTitle,
          timestamp: new Date().toISOString()
        };
        
        await recordInteraction(
          identifier,
          profile._id,
          activeSessionNo,
          activeSegment,
          currentSlideIndex,
          slide.SlideLayout,
          interactionData,
          INTERACTION_CONFIG[slide.SlideLayout].type
        );
        
        setActivityCompleted(prev => ({ ...prev, [slide.SlideID]: true }));
        console.log('Discussion session started successfully:', response);
        console.log('Updated discussion sessions:', updatedDiscussionSessions);
        
        // Update session data with new discussion sessions
        if (onSessionDataUpdate) {
          onSessionDataUpdate({ discussionSessions: updatedDiscussionSessions });
        }
        
        // Open discussion modal after successful save and data update
        // Use the special function for newly created sessions that includes the updated sessions array
        if (onDiscussionModalOpenWithNewSession) {
          onDiscussionModalOpenWithNewSession(slide.SlideID, updatedDiscussionSessions);
        } else if (onDiscussionModalOpen) {
          // Fallback to regular function
          onDiscussionModalOpen(slide.SlideID);
        }
      }
    } catch (error) {
      console.error('Error starting discussion:', error);
    } finally {
      setInteractionLoading(false);
    }
  };

  // Load existing interaction data for the current slide
  const loadSlideInteraction = async (slide) => {
    // Get profile - either from props or localStorage
    let currentProfile = profile;
    if (!currentProfile?._id) {
      currentProfile = getProfileFromStorage();
    }
    
    if (!slide || !currentProfile?._id || !identifier || !activeSessionNo || !activeSegment) return;

    // Check if this slide supports interactions
    const layoutType = slide.SlideLayout;
    if (!INTERACTION_CONFIG[layoutType]) return;

    try {
      setFetchingExistingRecord(true);
      setInputsDisabled(true); // Block inputs while loading
      const response = await getInteraction(
        identifier,
        currentProfile._id,
        activeSessionNo,
        activeSegment,
        currentSlideIndex,
        // Assessment answers are keyed by question, not slide position — the
        // question order is shuffled per learner, so position isn't stable.
        layoutType === 'ASSESSMENT_ACTIVITY' ? slide.assess_10 : undefined
      );

      if (response.data && response.data.exists) {
        const interactionData = response.data.interaction.interaction_data;
        setCurrentSlideInteraction(response.data.interaction);

        // Populate UI based on interaction type
        if (layoutType === 'JOURNALING_SELF_REFLECTION_ACTIVITY') {
          setJournalingText(interactionData || '');
          setActivityCompleted(prev => ({ ...prev, [slide.SlideID]: true }));
        } else if (layoutType === 'SHARE_WORK_FILE_ACTIVITY') {
          setShareWorkLink(interactionData || '');
          setActivityCompleted(prev => ({ ...prev, [slide.SlideID]: true }));
        } else if (layoutType === 'POLL_QUIZ_ACTIVITY') {
          if (interactionData && typeof interactionData === 'object') {
            const isMAQ = slide.Category11 === 'MAQ';
            
            if (isMAQ) {
              // Multiple Answer Question - restore selected options array
              if (interactionData.selectedOptions && Array.isArray(interactionData.selectedOptions)) {
                setSelectedPollOptions(interactionData.selectedOptions);
                setActivityCompleted(prev => ({ ...prev, [slide.SlideID]: true }));
              }
            } else {
              // Multiple Choice Question - restore single selected option
              if (interactionData.selectedOption !== undefined) {
                setSelectedPollOption(interactionData.selectedOption);
                setActivityCompleted(prev => ({ ...prev, [slide.SlideID]: true }));
              }
            }
          }
        } else if (layoutType === 'DISCUSSION_ACTIVITY') {
          if (interactionData && typeof interactionData === 'object' && interactionData.discussion_started) {
            setActivityCompleted(prev => ({ ...prev, [slide.SlideID]: true }));
          }
        } else if (layoutType === 'ASSESSMENT_ACTIVITY') {
          if (interactionData && typeof interactionData === 'object') {
            // Restore assessment answers based on question type
            const slideId = slide.SlideID;
            
            if (interactionData.questionType === 'MAQ' && interactionData.selectedOptions) {
              // Multiple Answer Question - restore selected options array
              setAssessmentAnswers(prev => ({ ...prev, [slideId]: interactionData.selectedOptions }));
              setActivityCompleted(prev => ({ ...prev, [slide.SlideID]: true }));
            } else if (interactionData.questionType === 'MTQ' && Array.isArray(interactionData.selectedOptions)) {
              // Matching/Ordering Question - restore the arrangement order
              setAssessmentAnswers(prev => ({ ...prev, [slideId]: interactionData.selectedOptions }));
              setActivityCompleted(prev => ({ ...prev, [slide.SlideID]: true }));
            } else if (interactionData.questionType === 'OPQ' && interactionData.freeTextAnswer) {
              // Open Question - restore free text answer
              setAssessmentAnswers(prev => ({ ...prev, [slideId]: interactionData.freeTextAnswer }));
              setActivityCompleted(prev => ({ ...prev, [slide.SlideID]: true }));
            } else if (interactionData.customInput) {
              // Custom input case
              setAssessmentAnswers(prev => ({ 
                ...prev, 
                [slideId]: {
                  customInput: interactionData.customInput,
                  selectedIndex: interactionData.selectedIndex
                }
              }));
              setActivityCompleted(prev => ({ ...prev, [slide.SlideID]: true }));
            } else if (interactionData.selectedOption !== undefined) {
              // Multiple Choice Question - restore single selected option
              setAssessmentAnswers(prev => ({ ...prev, [slideId]: interactionData.selectedOption }));
              setActivityCompleted(prev => ({ ...prev, [slide.SlideID]: true }));
            }
          }
        }
        
        // Keep inputs disabled if existing record found
        setInputsDisabled(true);
      } else {
        // No existing record found - enable inputs
        setInputsDisabled(false);
      }
    } catch (error) {
      console.error('Error loading slide interaction:', error);
      // Enable inputs on error
      setInputsDisabled(false);
    } finally {
      setFetchingExistingRecord(false);
    }
  };

  // Function to enable editing of completed activities
  const handleEditActivity = (slide) => {
    setInputsDisabled(false);
    setActivityCompleted(prev => ({ ...prev, [slide.SlideID]: false }));
  };

  // Utility function to check if an activity is required
  const isActivityRequired = (slide) => {
    const layoutType = slide?.SlideLayout;
    return INTERACTION_CONFIG[layoutType]?.required || false;
  };

  // Utility function to get activity config
  const getActivityConfig = (slide) => {
    const layoutType = slide?.SlideLayout;
    return INTERACTION_CONFIG[layoutType] || null;
  };

  // Load interaction data when slide changes
  useEffect(() => {
    if (sessionContent && activeSegment && Array.isArray(sessionContent[activeSegment]) && currentSlideIndex >= 0) {
      const currentSlide = sessionContent[activeSegment][currentSlideIndex];
      loadSlideInteraction(currentSlide);
    }
  }, [sessionContent, activeSegment, currentSlideIndex, activeSessionNo, profile?._id, identifier]);

  return {
    // State
    journalingText,
    shareWorkLink,
    selectedPollOption,
    selectedPollOptions,
    assessmentAnswers,
    activityCompleted,
    interactionLoading,
    fetchingExistingRecord,
    inputsDisabled,
    currentSlideInteraction,
    
    // Setters
    setJournalingText,
    setShareWorkLink,
    setSelectedPollOption,
    setSelectedPollOptions,
    setAssessmentAnswers,
    
    // Functions
    resetActivityState,
    handleJournalingSave,
    handleShareWorkSubmit,
    handlePollSubmit,
    handleAssessmentSubmit,
    handleDiscussionStart,
    loadSlideInteraction,
    handleEditActivity,
    isActivityRequired,
    getActivityConfig
  };
};
